if (!customElements.get("subscription-popup")) {
  customElements.define(
    "subscription-popup",
    class SubscriptionPopup extends HTMLElement {
      constructor() {
        super();

        this.overlay = this.querySelector("[data-overlay]");
        this.loader = this.querySelector("[data-loader]");
        this.content = this.querySelector("[data-content]");
        this.closeBtn = this.querySelector("[data-close]");
        this.mainImage = this.querySelector("[data-main-image]");
        this.thumbnails = this.querySelector("[data-thumbnails]");
        this.titleEl = this.querySelector("[data-title]");
        this.descriptionEl = this.querySelector("[data-description]");
        this.optionsContainer = this.querySelector("[data-options]");
        this.optionsList = this.querySelector("[data-options-list]");
        this.form = this.querySelector("[data-form]");
        this.variantInput = this.querySelector("[data-variant-id]");
        this.sellingPlanInput = this.querySelector("[data-selling-plan]");
        this.quantityInput = this.querySelector("[data-quantity]");
        this.submitBtn = this.querySelector("[data-submit]");
        this.errorEl = this.querySelector("[data-error]");

        this.product = null;
        this.variant = null;
        this.fetchController = null;
        this.pendingHandle = null;
        this.cache = {};
        this.quickAddProxy = document.querySelector(
          "quick-add[data-subscription-popup-quick-add]",
        );

        this.onOverlayClick = this.close.bind(this);
        this.onCloseClick = this.close.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onSubmit = this.handleSubmit.bind(this);
        this.onTriggerClick = this.handleTriggerClick.bind(this);
        this.onProviderSet = this.handleProviderSet.bind(this);
      }

      connectedCallback() {
        this.overlay.addEventListener("click", this.onOverlayClick);
        this.closeBtn.addEventListener("click", this.onCloseClick);
        this.form.addEventListener("submit", this.onSubmit);
        document.addEventListener("keydown", this.onKeydown);
        document.addEventListener("click", this.onTriggerClick);
        window.addEventListener("dtcCookieSet", this.onProviderSet);
        this.initButtonVisibility();
      }

      disconnectedCallback() {
        this.overlay.removeEventListener("click", this.onOverlayClick);
        this.closeBtn.removeEventListener("click", this.onCloseClick);
        this.form.removeEventListener("submit", this.onSubmit);
        document.removeEventListener("keydown", this.onKeydown);
        document.removeEventListener("click", this.onTriggerClick);
        window.removeEventListener("dtcCookieSet", this.onProviderSet);
      }

      // --- State helpers ---

      get isOpen() {
        return this.classList.contains("is-open");
      }

      get cart() {
        return document.querySelector("cart-drawer");
      }

      // --- Public API ---

      async open(handle) {
        if (this.openQuickAdd(handle)) {
          this.publish("subscriptionPopupOpen", { handle, mode: "quick-buy" });
          return;
        }

        this.showModal();
        this.showLoader();

        if (this.fetchController) this.fetchController.abort();
        this.fetchController = new AbortController();

        try {
          const product = await this.fetchProduct(handle);
          this.product = product;
          this.subscriptionVariant =
            product.variants.find((v) => v.selling_plan_allocations?.length > 0) ||
            product.variants[0];
          this.onetimeVariant = product.variants[0];
          this.variant = this.subscriptionVariant;
          this.variantInput.value = this.variant.id;
          this.quantityInput.value = 1;
          this.render();
          this.hideLoader();
          this.closeBtn.focus();
        } catch (err) {
          if (err.name === "AbortError") return;
          console.error("Subscription popup:", err);
          this.hideLoader();
          this.showError("Failed to load product. Please try again.");
        }

        this.publish("subscriptionPopupOpen", { handle });
      }

      close() {
        this.classList.remove("is-open");
        document.body.classList.remove("subscription-popup-open");
        this.errorEl.hidden = true;
        this.setLoading(false);
        this.product = null;
        this.variant = null;
        this.publish("subscriptionPopupClose", {});
      }

      // --- Data ---

      async fetchProduct(handle) {
        if (this.cache[handle]) return this.cache[handle];

        const response = await fetch(`/products/${handle}.js`, {
          signal: this.fetchController.signal,
        });
        const product = await response.json();
        this.cache[handle] = product;
        return product;
      }

      getSellingPlans() {
        const plans = [];
        const variant = this.variant;

        if (!this.product.selling_plan_groups) return plans;

        for (const group of this.product.selling_plan_groups) {
          for (const plan of group.selling_plans) {
            const allocation = variant.selling_plan_allocations?.find(
              (a) => a.selling_plan_id === plan.id,
            );
            if (allocation) {
              plans.push({ plan, allocation, groupName: group.name });
            }
          }
        }
        return plans;
      }

      // --- Rendering ---

      render() {
        this.titleEl.textContent = this.product.title;
        this.descriptionEl.textContent = this.truncateText(
          this.stripHtml(this.product.description || ""),
          30,
        );
        this.renderGallery();
        this.renderOptions();
      }

      renderGallery() {
        this.mainImage.innerHTML = "";
        this.thumbnails.innerHTML = "";

        const images = this.product.images;
        if (!images?.length) return;

        const mainImg = this.createEl("img", {
          src: this.sizedUrl(this.imageSrc(images[0]), 600),
          alt: this.product.title,
          loading: "eager",
        });
        this.mainImage.appendChild(mainImg);

        if (images.length <= 1) return;

        images.forEach((image, i) => {
          const src = this.imageSrc(image);
          const thumb = this.createEl("button", {
            type: "button",
            className: "subscription-popup__thumbnail" + (i === 0 ? " is-active" : ""),
            "aria-label": `View image ${i + 1}`,
          });
          thumb.appendChild(
            this.createEl("img", {
              src: this.sizedUrl(src, 200),
              alt: "",
              loading: "lazy",
            }),
          );
          thumb.addEventListener("click", () => {
            mainImg.src = this.sizedUrl(src, 600);
            this.thumbnails.querySelectorAll(".subscription-popup__thumbnail").forEach((t) =>
              t.classList.remove("is-active"),
            );
            thumb.classList.add("is-active");
          });
          this.thumbnails.appendChild(thumb);
        });
      }

      renderOptions() {
        this.optionsList.innerHTML = "";
        const plans = this.getSellingPlans();

        if (!plans.length) {
          this.optionsContainer.style.display = "none";
          this.sellingPlanInput.value = "";
          this.quantityInput.value = 1;
          return;
        }

        this.optionsContainer.style.display = "";

        plans.forEach((item) => {
          const quantity = this.getPlanQuantity(item.plan);
          const compareUnitPrice =
            item.allocation.compare_at_price ||
            this.subscriptionVariant.compare_at_price ||
            this.subscriptionVariant.price;
          const totalPrice = item.allocation.price * quantity;
          const totalComparePrice = compareUnitPrice * quantity;

          this.optionsList.appendChild(
            this.createOption({
              sellingPlanId: item.plan.id,
              variantId: this.subscriptionVariant.id,
              quantity,
              label: item.groupName || item.plan.name || "Subscribe + Save",
              description: this.buildPlanDescription(item.plan, quantity),
              price: formatMoney(totalPrice),
              comparePrice: totalComparePrice > totalPrice ? formatMoney(totalComparePrice) : "",
              badge: this.buildPlanBadge(item.plan, totalComparePrice - totalPrice, quantity),
            }),
          );
        });

        this.optionsList.appendChild(
          this.createOption({
            sellingPlanId: "",
            variantId: this.onetimeVariant.id,
            quantity: 1,
            label: "One-Time Purchase",
            description: "",
            price: formatMoney(this.onetimeVariant.price),
            comparePrice: "",
            badge: "",
          }),
        );

        this.selectFirstOption();
      }

      createOption({
        sellingPlanId,
        variantId,
        quantity,
        label,
        description,
        price,
        comparePrice,
        badge,
      }) {
        const el = this.createEl("label", {
          className: "subscription-popup__option",
          "data-selling-plan-id": sellingPlanId,
          "data-variant-id": variantId,
          "data-quantity": quantity,
        });

        el.innerHTML = `
          <input type="radio" name="delivery_option" value="${sellingPlanId || "onetime"}">
          <span class="subscription-popup__radio"></span>
          <div class="subscription-popup__option-content">
            <span class="subscription-popup__option-label">${this.escapeHtml(label)}</span>
            ${description ? `<div class="subscription-popup__option-description">${this.escapeHtml(description)}</div>` : ""}
          </div>
          <div class="subscription-popup__option-prices">
            ${badge ? `<span class="subscription-popup__savings-badge">${this.escapeHtml(badge)}</span>` : ""}
            <span class="subscription-popup__option-price">${this.escapeHtml(price)}</span>
            ${comparePrice ? `<span class="subscription-popup__option-compare-price">${this.escapeHtml(comparePrice)}</span>` : ""}
          </div>
        `;

        el.addEventListener("click", () => this.selectOption(el));
        return el;
      }

      // --- Option selection ---

      selectOption(el) {
        this.optionsList.querySelectorAll(".subscription-popup__option").forEach((opt) => {
          opt.classList.remove("is-selected");
          opt.querySelector("input").checked = false;
        });
        el.classList.add("is-selected");
        el.querySelector("input").checked = true;
        this.sellingPlanInput.value = el.dataset.sellingPlanId || "";
        this.variantInput.value = el.dataset.variantId || this.variant.id;
        this.quantityInput.value = el.dataset.quantity || 1;
      }

      selectFirstOption() {
        const first = this.optionsList.querySelector(".subscription-popup__option");
        if (first) this.selectOption(first);
      }

      // --- Cart submission ---

      async handleSubmit(evt) {
        evt.preventDefault();
        if (this.submitBtn.getAttribute("aria-disabled") === "true") return;

        this.errorEl.hidden = true;
        this.setLoading(true);

        const formData = new FormData(this.form);
        if (!formData.get("selling_plan")) formData.delete("selling_plan");

        if (this.cart?.getSectionsToRender) {
          formData.append("sections", this.cart.getSectionsToRender().map((s) => s.id));
        }
        formData.append("sections_url", window.location.pathname);

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];
        config.body = formData;

        try {
          const res = await fetch(window.routes.cart_add_url, config);
          const response = await res.json();

          if (response.status) {
            this.showError(response.description || response.message || "Could not add to cart.");
            this.setLoading(false);
            this.publish("cartError", {
              source: "subscription-popup",
              productVariantId: formData.get("id"),
              errors: response.errors || response.description,
              message: response.message,
            });
            return;
          }

          this.publish("cartUpdate", {
            source: "subscription-popup",
            productVariantId: formData.get("id"),
            cartData: response,
          });

          this.close();

          if (this.cart?.renderContents) {
            const cartType = this.cart.dataset.cartType || "page";
            if (cartType === "drawer") {
              this.cart.renderContents(response, true);
              return;
            }
          }
          window.location = window.routes.cart_url;
        } catch (err) {
          console.error("Subscription popup:", err);
          this.showError("Something went wrong. Please try again.");
          this.setLoading(false);
        }
      }

      // --- Event handlers ---

      handleKeydown(e) {
        if (e.key === "Escape" && this.isOpen) this.close();
      }

      handleTriggerClick(e) {
        const trigger = e.target.closest("[data-subscription-popup-trigger]");
        if (!trigger) return;

        e.preventDefault();
        const handle = trigger.dataset.productHandle;
        if (!handle) return;

        if (this.isProviderSelected()) {
          this.open(handle);
          return;
        }

        // On a provider page — scroll to hero so user can select provider
        const selectBtn = document.querySelector("[data-microsite-select-btn]");
        if (selectBtn) {
          this.scrollToProvider(handle);
          return;
        }

        // Non-provider page — open drawer directly without requiring a provider
        this.open(handle);
      }

      handleProviderSet() {
        this.initButtonVisibility();

        // If user just selected a provider after clicking a product button,
        // automatically open the popup for the product they were viewing
        if (this.pendingHandle && this.isProviderSelected()) {
          const handle = this.pendingHandle;
          this.pendingHandle = null;
          setTimeout(() => this.open(handle), 500);
        }
      }

      // --- Provider helpers ---

      isProviderSelected() {
        if (!window.DTCCookieManager) return true;
        const loc = window.DTCCookieManager.getCookie("location_id");
        const prog = window.DTCCookieManager.getCookie("program_id");
        return !!(loc || prog);
      }

      scrollToProvider(handle) {
        this.pendingHandle = handle;
        const btn = document.querySelector("[data-microsite-select-btn]");
        const target = btn || document.querySelector(".microsite-hero");
        if (!target) return;

        target.scrollIntoView({ behavior: "smooth", block: "center" });

        if (btn) {
          btn.classList.add("subscription-popup-highlight");
          setTimeout(() => btn.classList.remove("subscription-popup-highlight"), 3000);
        }
      }

      openQuickAdd(handle) {
        if (
          !this.quickAddProxy ||
          typeof this.quickAddProxy.fetchProduct !== "function" ||
          !handle
        ) {
          return false;
        }

        this.quickAddProxy.dataset.quickAddMode = "microsite-product";
        this.quickAddProxy.fetchProduct(`/products/${handle}`);
        return true;
      }

      initButtonVisibility() {
        const isProviderPage = !!document.querySelector("[data-microsite-select-btn]");
        const providerSelected = this.isProviderSelected();

        document.querySelectorAll("[data-subscription-popup-trigger]").forEach((trigger) => {
          const fallback =
            trigger.parentElement?.querySelector("[data-find-provider-fallback]");

          if (providerSelected) {
            // Provider selected — show original product buttons everywhere
            trigger.style.display = "";
            if (fallback) fallback.style.display = "none";
            // Restore original text if it was swapped
            if (trigger.dataset.originalText) {
              trigger.textContent = trigger.dataset.originalText;
              delete trigger.dataset.originalText;
            }
          } else if (isProviderPage) {
            // Provider page but not yet selected — swap button text to "Select a Provider"
            trigger.style.display = "";
            if (fallback) fallback.style.display = "none";
            if (!trigger.dataset.originalText) {
              trigger.dataset.originalText = trigger.textContent;
            }
            trigger.textContent = "Select a Provider";
          } else {
            // Non-provider page, no provider — show product button directly (no provider required)
            trigger.style.display = "";
            if (fallback) fallback.style.display = "none";
            if (trigger.dataset.originalText) {
              trigger.textContent = trigger.dataset.originalText;
              delete trigger.dataset.originalText;
            }
          }
        });
      }

      // --- UI helpers ---

      showModal() {
        this.classList.add("is-open");
        document.body.classList.add("subscription-popup-open");
        this.errorEl.hidden = true;
      }

      showLoader() {
        this.loader.classList.remove("hidden");
        this.content.style.display = "none";
      }

      hideLoader() {
        this.loader.classList.add("hidden");
        this.content.style.display = "";
      }

      setLoading(on) {
        this.submitBtn.toggleAttribute("aria-disabled", on);
        this.submitBtn.classList.toggle("loading", on);
        this.submitBtn.querySelector(".loading-overlay__spinner").classList.toggle("hidden", !on);
      }

      showError(msg) {
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
      }

      publish(eventName, data) {
        if (typeof window.publish === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS[eventName]) {
          window.publish(PUB_SUB_EVENTS[eventName], data);
        }
      }

      // --- Utility ---

      createEl(tag, attrs) {
        const el = document.createElement(tag);
        for (const [key, val] of Object.entries(attrs)) {
          if (key === "className") el.className = val;
          else el.setAttribute(key, val);
        }
        return el;
      }

      escapeHtml(str) {
        const div = document.createElement("div");
        div.appendChild(document.createTextNode(str));
        return div.innerHTML;
      }

      stripHtml(html) {
        return new DOMParser().parseFromString(html, "text/html").body.textContent || "";
      }

      truncateText(text, wordCount) {
        const words = text.trim().split(/\s+/);
        return words.length <= wordCount ? text : words.slice(0, wordCount).join(" ") + "...";
      }

      imageSrc(image) {
        return typeof image === "string" ? image : image?.src || "";
      }

      sizedUrl(src, width) {
        if (!src) return "";
        let url = src.startsWith("//") ? "https:" + src : src;
        return url + (url.includes("?") ? "&" : "?") + "width=" + width;
      }

      getPlanOptionValue(plan, optionName) {
        return (
          plan.options?.find((option) => option.name === optionName)?.value || ""
        );
      }

      getPlanQuantity(plan) {
        const quantity = Number.parseInt(this.getPlanOptionValue(plan, "Product Quantity"), 10);
        return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      }

      getPlanFrequency(plan) {
        const rawValue = this.getPlanOptionValue(plan, "Order Frequency and Unit");
        if (!rawValue) return "";

        const [countValue, unitValue] = rawValue.split("-");
        const count = Number.parseInt(countValue, 10);
        if (!Number.isFinite(count) || !unitValue) return rawValue;

        const unitMap = {
          day: ["day", "days"],
          week: ["week", "weeks"],
          month: ["month", "months"],
          year: ["year", "years"],
        };
        const [singularUnit, pluralUnit] = unitMap[unitValue] || [unitValue, `${unitValue}s`];

        if (count === 1) return `Every ${singularUnit}`;
        return `Every ${count} ${pluralUnit}`;
      }

      buildPlanDescription(plan, quantity) {
        const quantityText = `${quantity} ${quantity === 1 ? "item" : "items"}`;
        const frequencyText = this.getPlanFrequency(plan);
        if (!frequencyText) return quantityText;

        return `${quantityText} ${String.fromCharCode(8226)} ${frequencyText}`;
      }

      buildPlanBadge(plan, savingsAmount, quantity) {
        const adjustment = plan.price_adjustments?.[0];
        if (!adjustment) return "";

        if (adjustment.value_type === "percentage" && adjustment.value) {
          return `Save ${adjustment.value}%`;
        }

        if (adjustment.value_type === "fixed_amount" && adjustment.value) {
          return `Save ${formatMoney(adjustment.value * quantity)}`;
        }

        if (savingsAmount > 0) {
          return `Save ${formatMoney(savingsAmount)}`;
        }

        return "";
      }
    },
  );
}

// Standalone money formatter (used by class and available globally if needed)
function formatMoney(cents) {
  if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
    return Shopify.formatMoney(cents);
  }
  return "$" + (cents / 100).toFixed(2);
}
